import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Group,
  ScrollArea,
  Button,
  Badge,
  Chips,
  Chip,
  Divider,
} from '@mantine/core';
import { Settings } from 'tabler-icons-react';
import ObjectActionCreators from '../../../actions';

const normalizeObjKey = (o) => o?.key || o?.label || o?.objTypeId || '';
const getFields = (objDef) =>
  Array.isArray(objDef?.objFormData) ? objDef.objFormData : [];

export default function FilterConsoleConfigurator({
  size = 'lg',
  iconOnly = true,
}) {
  const dispatch = useDispatch();
  const objectsDefList = useSelector((s) => s.objects.objectsDefList) || [];
  const currentConfig = useSelector((s) => s.objects.viewFiltersConfig) || [];

  const [opened, setOpened] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState(null);

  // Map: objTypeId -> string[] (ordre d'insertion conservé)
  const initialByType = useMemo(() => {
    const map = new Map();
    (currentConfig || []).forEach(({ objTypeId, fieldNames }) => {
      map.set(objTypeId, Array.isArray(fieldNames) ? [...fieldNames] : []);
    });
    return map;
  }, [currentConfig]);

  const initialTypeOrder = useMemo(
    () => (currentConfig || []).map((x) => x.objTypeId),
    [currentConfig]
  );

  // État local modifiable
  const [byType, setByType] = useState(initialByType);

  // Liste triée des types (clé affichable + id)
  const types = useMemo(() => {
    const list = objectsDefList.map((o) => ({
      id: normalizeObjKey(o),
      label: o?.label || o?.key || '—',
      obj: o,
    }));
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [objectsDefList]);

  // Type sélectionné par défaut
  const effectiveSelected = useMemo(() => {
    if (selectedTypeId) return selectedTypeId;
    return types[0]?.id ?? null;
  }, [selectedTypeId, types]);

  const selectedObj = useMemo(
    () =>
      objectsDefList.find((o) => normalizeObjKey(o) === effectiveSelected) ||
      null,
    [objectsDefList, effectiveSelected]
  );

  const selectedFields = useMemo(() => getFields(selectedObj), [selectedObj]);

  // valeurs cochées (chips) pour le type courant
  const selectedNamesForType = useMemo(() => {
    return [...(byType.get(effectiveSelected) || [])];
  }, [byType, effectiveSelected]);

  const toggleField = (fieldName) => {
    setByType((prev) => {
      const next = new Map(prev);
      const arr = [...(next.get(effectiveSelected) || [])];
      const idx = arr.indexOf(fieldName);
      if (idx >= 0) {
        // remove en conservant l'ordre des restants
        arr.splice(idx, 1);
      } else {
        // append à la fin
        arr.push(fieldName);
      }
      next.set(effectiveSelected, arr);
      return next;
    });
  };

  const clearType = () => {
    setByType((prev) => {
      const next = new Map(prev);
      next.set(effectiveSelected, []);
      return next;
    });
  };

  const clearAll = () => setByType(new Map());

  const onSave = () => {
    const selectedTypeIds = types
      .map(({ id }) => id)
      .filter((id) => (byType.get(id) || []).length > 0);

    // 1) ordre initial (ceux déjà présents dans currentConfig)
    const orderedTypeIds = [
      ...initialTypeOrder.filter((id) => selectedTypeIds.includes(id)),
      // 2) puis les nouveaux types sélectionnés non présents initialement
      ...selectedTypeIds.filter((id) => !initialTypeOrder.includes(id)),
    ];

    // Reconstruire payload en respectant l'ordre ET l'ordre des champs
    const payload = orderedTypeIds.map((id) => ({
      objTypeId: id,
      fieldNames: [...(byType.get(id) || [])], // déjà ordonné par nos toggles
    }));
    dispatch(ObjectActionCreators.updateViewFiltersConfig(payload));
    setOpened(false);
  };

  return (
    <>
      <Button
        variant="subtle"
        onClick={() => {
          // (ré-initialise l’état local à chaque ouverture)
          setByType(new Map(initialByType));
          setOpened(true);
        }}
        leftIcon={iconOnly ? <Settings size={18} /> : null}
      >
        {iconOnly ? null : 'Configurer la vue'}
      </Button>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        size={size}
        title="Champs de filtrage par type"
      >
        <Group align="start" spacing="md" grow>
          {/* Panneau gauche : types */}
          <div style={{ width: 280, minWidth: 240, maxHeight: 420 }}>
            <ScrollArea style={{ height: 420, paddingRight: 6 }}>
              {types.map(({ id, label }) => {
                const count = (byType.get(id) || new Set()).size;
                const isActive = id === effectiveSelected;
                return (
                  <div
                    key={id}
                    onClick={() => setSelectedTypeId(id)}
                    style={{
                      cursor: 'pointer',
                      padding: '6px 8px',
                      marginBottom: 4,
                      borderRadius: 8,
                      background: isActive ? '#E7F5FF' : 'transparent',
                      border: isActive ? '1px solid #228BE6' : '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{label}</span>
                    {count > 0 && (
                      <Badge size="sm" variant="filled">
                        {count}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </ScrollArea>
          </div>

          <Divider orientation="vertical" />

          {/* Panneau droit : champs pour le type sélectionné */}
          <div style={{ flex: 1, maxHeight: 420 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <strong>Champs filtrables</strong>
              <Group spacing="xs">
                <Button size="xs" variant="outline" onClick={clearType}>
                  Vider ce type
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  onClick={clearAll}
                >
                  Tout vider
                </Button>
              </Group>
            </div>
            <ScrollArea style={{ height: 372 }}>
              <Chips multiple value={selectedNamesForType}>
                {selectedFields.map((f) => (
                  <Chip
                    key={f.attrName}
                    value={f.attrName}
                    onClick={() => toggleField(f.attrName)}
                  >
                    {f.label || f.attrName}
                  </Chip>
                ))}
              </Chips>
            </ScrollArea>
          </div>
        </Group>

        <Group position="right" mt="md">
          <Button variant="default" onClick={() => setOpened(false)}>
            Annuler
          </Button>
          <Button onClick={onSave}>Enregistrer</Button>
        </Group>
      </Modal>
    </>
  );
}
